import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "./Toast";

type Format = "pdf" | "xlsx";

type Props = {
  /** Order id (UUID) used to build /api/exports/orders/{id}.{ext} */
  orderId: string;
  /** Order number used for the suggested filename */
  orderNumber: string;
};

/**
 * Modal-fronted download. Trigger button opens an overlay with PDF/Excel
 * radios; on submit we fetch the chosen format and stream it as a blob
 * to the browser (same auth-cookie pattern as ExportButton).
 */
export function DownloadModal({ orderId, orderNumber }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [format, setFormat] = useState<Format>("pdf");
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(`/api/exports/orders/${orderId}.${format}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Export failed: ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `order-${orderNumber}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      setOpen(false);
    } catch (e) {
      console.error(e);
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button type="button" className="btn" onClick={() => setOpen(true)}>
        {t("order.download.button")}
      </button>
      {open && (
        <div role="dialog" aria-modal="true" style={overlay} onClick={() => !busy && setOpen(false)}>
          <div style={panel} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 12px" }}>{t("order.download.title")}</h3>
            <label style={radioRow}>
              <input
                type="radio"
                name="dl-format"
                checked={format === "pdf"}
                onChange={() => setFormat("pdf")}
              />
              <span>{t("order.download.format.pdf")}</span>
            </label>
            <label style={radioRow}>
              <input
                type="radio"
                name="dl-format"
                checked={format === "xlsx"}
                onChange={() => setFormat("xlsx")}
              />
              <span>{t("order.download.format.xlsx")}</span>
            </label>
            <div style={actions}>
              <button type="button" className="btn" onClick={() => setOpen(false)} disabled={busy}>
                {t("order.download.cancel")}
              </button>
              <button type="button" className="btn primary" onClick={submit} disabled={busy}>
                {busy ? t("order.download.busy") : t("order.download.submit")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const overlay: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "var(--overlay)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 50,
};

const panel: React.CSSProperties = {
  background: "var(--surface)",
  borderRadius: 10,
  padding: 20,
  width: 360,
  boxShadow: "var(--shadow-2)",
};

const radioRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "8px 0",
  fontSize: 14,
  cursor: "pointer",
};

const actions: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 8,
  marginTop: 16,
};
